<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="4">
        <v-card>
          <v-card-title tag="h1" class="text-headline-small text-center pt-6">
            {{ title }}
          </v-card-title>

          <v-card-text>
            <!-- Sign In / Sign Up Form -->
            <v-form v-if="mode !== 'reset'" @submit.prevent="submit">
              <v-text-field
                v-if="mode === 'signup'"
                v-model="displayName"
                name="displayName"
                label="Display name"
                prepend-icon="mdi-account"
              />
              <v-text-field
                v-model="email"
                name="email"
                label="Email"
                type="email"
                prepend-icon="mdi-email"
              />
              <v-text-field
                v-model="password"
                name="password"
                label="Password"
                prepend-icon="mdi-lock"
                :type="showPassword ? 'text' : 'password'"
              >
                <!-- A named toggle; Vuetify's icon would be read out as
                     "Password appended action" -->
                <template v-slot:append-inner>
                  <v-btn
                    :icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :aria-label="
                      showPassword ? 'Hide password' : 'Show password'
                    "
                    :aria-pressed="showPassword"
                    variant="text"
                    density="comfortable"
                    color="on-surface"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </v-text-field>

              <div class="d-flex align-center justify-end mb-4">
                <a
                  class="text-body-small text-decoration-none text-primary"
                  href="#"
                  @click.prevent="switchMode('reset')"
                >
                  Forgot password?
                </a>
              </div>

              <v-alert
                v-if="error"
                type="error"
                variant="tonal"
                class="mb-4"
                density="compact"
              >
                {{ error }}
              </v-alert>

              <v-btn
                type="submit"
                color="primary"
                block
                :loading="loading"
                :disabled="loading"
              >
                {{ mode === 'signin' ? 'Sign in' : 'Sign up' }}
              </v-btn>

              <div class="d-flex align-center my-4">
                <v-divider />
                <span class="text-body-small mx-3">or</span>
                <v-divider />
              </div>

              <v-btn
                variant="outlined"
                prepend-icon="mdi-google"
                block
                :disabled="loading"
                @click="signInWithGoogle"
              >
                Continue with Google
              </v-btn>

              <div class="text-body-small text-center mt-4">
                <template v-if="mode === 'signin'">
                  Don't have an account?
                  <a
                    class="text-primary"
                    href="#"
                    @click.prevent="switchMode('signup')"
                  >
                    Sign up
                  </a>
                </template>
                <template v-else>
                  Already have an account?
                  <a
                    class="text-primary"
                    href="#"
                    @click.prevent="switchMode('signin')"
                  >
                    Sign in
                  </a>
                </template>
              </div>
            </v-form>

            <!-- Password Reset Form -->
            <v-form v-else @submit.prevent="resetPassword">
              <p class="text-body-medium mb-4">
                Enter your email address and we'll send you a link to reset your
                password.
              </p>
              <v-text-field
                v-model="email"
                name="email"
                label="Email"
                type="email"
                prepend-icon="mdi-email"
              />

              <v-alert
                v-if="error"
                type="error"
                variant="tonal"
                class="mb-4"
                density="compact"
              >
                {{ error }}
              </v-alert>

              <v-alert
                v-if="resetSent"
                type="success"
                variant="tonal"
                class="mb-4"
                density="compact"
              >
                Password reset email sent. Check your inbox.
              </v-alert>

              <v-btn
                type="submit"
                color="primary"
                block
                :loading="loading"
                :disabled="loading || resetSent"
              >
                Send reset email
              </v-btn>

              <div class="text-body-small text-center mt-4">
                Back to
                <a
                  class="text-primary"
                  href="#"
                  @click.prevent="switchMode('signin')"
                >
                  Sign in
                </a>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
  import { defineComponent, computed, ref } from 'vue';
  import {
    GoogleAuthProvider,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    updateProfile,
  } from 'firebase/auth';
  import { useFirebaseAuth } from 'vuefire';
  import { useRouter } from 'vue-router';

  type Mode = 'signin' | 'signup' | 'reset';

  const errorMessages: Record<string, string> = {
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Invalid email or password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/too-many-requests':
      'Too many attempts. Please try again in a moment.',
    'auth/account-exists-with-different-credential':
      'An account with this email already exists. Sign in with your password.',
    'auth/popup-blocked': 'Allow pop-ups to sign in with Google.',
  };

  // Closing the Google window is not an error
  const cancelled = [
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
  ];

  export default defineComponent({
    name: 'LoginView',
    setup() {
      const auth = useFirebaseAuth()!;
      const router = useRouter();
      const redirectUrl = String(
        router.currentRoute.value.query['redirect'] || '/',
      );

      const mode = ref<Mode>('signin');
      const email = ref('');
      const password = ref('');
      const displayName = ref('');
      const showPassword = ref(false);
      const loading = ref(false);
      const error = ref('');
      const resetSent = ref(false);

      const title = computed(() => {
        if (mode.value === 'signin') return 'Sign in';
        if (mode.value === 'signup') return 'Create account';
        return 'Reset password';
      });

      function handleError(e: unknown) {
        const code = (e as { code?: string }).code || '';
        error.value = errorMessages[code] || 'An unexpected error occurred.';
      }

      function switchMode(newMode: Mode) {
        mode.value = newMode;
        error.value = '';
        resetSent.value = false;
      }

      async function submit() {
        error.value = '';

        if (!email.value || !password.value) {
          error.value = 'Please fill in all fields.';
          return;
        }

        if (mode.value === 'signup' && !displayName.value.trim()) {
          error.value = 'Please enter a display name.';
          return;
        }

        loading.value = true;
        try {
          if (mode.value === 'signin') {
            await signInWithEmailAndPassword(auth, email.value, password.value);
          } else {
            const credential = await createUserWithEmailAndPassword(
              auth,
              email.value,
              password.value,
            );
            await updateProfile(credential.user, {
              displayName: displayName.value.trim(),
            });
          }
          await router.push(redirectUrl);
        } catch (e) {
          handleError(e);
        } finally {
          loading.value = false;
        }
      }

      // The profile and own family group are made on first sign-in, as for
      // email sign-up (UserService.init in AuthenticatedRoute)
      async function signInWithGoogle() {
        error.value = '';
        loading.value = true;
        try {
          await signInWithPopup(auth, new GoogleAuthProvider());
          await router.push(redirectUrl);
        } catch (e) {
          if (!cancelled.includes((e as { code?: string }).code ?? '')) {
            handleError(e);
          }
        } finally {
          loading.value = false;
        }
      }

      async function resetPassword() {
        error.value = '';

        if (!email.value) {
          error.value = 'Please enter your email address.';
          return;
        }

        loading.value = true;
        try {
          await sendPasswordResetEmail(auth, email.value);
          resetSent.value = true;
        } catch (e) {
          handleError(e);
        } finally {
          loading.value = false;
        }
      }

      return {
        mode,
        email,
        password,
        displayName,
        showPassword,
        loading,
        error,
        resetSent,
        title,
        switchMode,
        submit,
        signInWithGoogle,
        resetPassword,
      };
    },
  });
</script>
